import { useKeycloak } from '@react-keycloak/web';
import { useMemo } from 'react';

import { AxiosProgressEvent } from 'axios';

import useUploadedFilesStore from '@/shared/api/uploadedFiles';

interface CachedApi {
  token: string | undefined;
  api: any;
}

// One axios instance per (ApiClass, token). Shared across components so we
// don't stack identical instances+interceptors when many components mount.
const apiCache = new WeakMap<any, CachedApi>();

const UPLOAD_PROGRESS_CAP = 85;

const attachProgressInterceptor = (instance: any) => {
  instance.interceptors.request.use((config: any) => {
    if (
      config.headers?.['Content-Type'] !== 'multipart/form-data' ||
      !(config.data instanceof FormData)
    ) {
      return config;
    }

    const { appendFile, setProgress } = useUploadedFilesStore.getState();
    const filesData: { id: string; size: number }[] = [];

    for (const [, value] of Array.from(config.data.entries())) {
      if (value instanceof File) {
        const fileId = `${value.name}-${Date.now()}-${Math.random()}`;
        filesData.push({ id: fileId, size: value.size });
        appendFile(value, fileId);
      }
    }

    if (filesData.length === 0) return config;

    config._uploadFileIds = filesData.map((f) => f.id);

    const cumulative: number[] = [];
    let totalSize = 0;
    filesData.forEach((fileData, index) => {
      totalSize += fileData.size;
      cumulative[index] = totalSize;
    });

    return {
      ...config,
      onUploadProgress: (progress: AxiosProgressEvent) => {
        const loaded = progress.loaded || 0;
        filesData.forEach((fileData, index) => {
          const start = index === 0 ? 0 : cumulative[index - 1];
          const end = cumulative[index];
          const loadedForFile = Math.min(Math.max(loaded - start, 0), end - start);
          const rawPercent =
            fileData.size > 0
              ? Math.min(100, Math.max(0, (loadedForFile / fileData.size) * 100))
              : 0;
          const cappedPercent = Math.min(rawPercent, UPLOAD_PROGRESS_CAP);
          setProgress(fileData.id, Number(cappedPercent.toFixed(2)));
        });
      },
    };
  });

  instance.interceptors.response.use(
    (response: any) => {
      const fileIds: string[] | undefined = response.config?._uploadFileIds;
      if (fileIds?.length) {
        const { setProgress } = useUploadedFilesStore.getState();
        fileIds.forEach((id) => setProgress(id, 100));
      }
      return response;
    },
    (error: any) => {
      const fileIds: string[] | undefined = error.config?._uploadFileIds;
      if (fileIds?.length) {
        const { removeFile } = useUploadedFilesStore.getState();
        fileIds.forEach((id) => removeFile(id));
      }
      return Promise.reject(error);
    },
  );
};

const useUploadProgressService = (ApiClass: any) => {
  const { keycloak } = useKeycloak();
  const token = keycloak.token;

  return useMemo(() => {
    const cached = apiCache.get(ApiClass);
    if (cached && cached.token === token) return cached.api;

    const api = new ApiClass({ headers: { Authorization: `Bearer ${token}` } });
    attachProgressInterceptor(api.instance);
    apiCache.set(ApiClass, { token, api });
    return api;
  }, [ApiClass, token]);
};

export default useUploadProgressService;
